import { useEffect } from 'react';
import { useEventDetails } from '../../hooks/useEventDetails';
import { V1BackgroundStyleProvider } from '../v1/components/V1BackgroundStyleProvider';

interface BackgroundStyleWrapperProps {
  eventSlug: string;
  style?: {
    backgroundColor?: string;
    backgroundUrl?: string;
    backgroundPosition?: string;
    backgroundTiled?: boolean;
  };
  children: React.ReactNode;
}

/**
 * BackgroundStyleWrapper determines which background style provider to use
 * based on whether the event is V1 or V2.
 * 
 * - V1 events: Use V1BackgroundStyleProvider with Rails-compatible logic
 * - V2 events: Use the default React logic (no special provider needed)
 * 
 * This ensures proper separation of concerns without conditional logic
 * in the main App component.
 */
export const BackgroundStyleWrapper: React.FC<BackgroundStyleWrapperProps> = ({ 
  eventSlug, 
  style, 
  children 
}) => {
  const { data: eventDetails } = useEventDetails(eventSlug);
  const isV1Event = eventDetails?.isV1Event || false;

  // V2 background styles effect - must always be called to satisfy hooks rules
  useEffect(() => {
    // Skip if V1 event or no style
    if (isV1Event || !style) return;

    const body = document.body;

    // Reset body styles
    body.style.margin = '0';
    body.style.padding = '0';
    body.style.minHeight = '100vh';

    // Apply background color
    if (style.backgroundColor) {
      body.style.backgroundColor = style.backgroundColor;
    }

    // Apply background image (V2 logic - simpler than V1)
    if (style.backgroundUrl) {
      body.style.backgroundImage = `url(${style.backgroundUrl})`;

      // V2 uses simpler center-based positioning
      const bgPosition = style.backgroundPosition === 'left' ? 'left center' :
                        style.backgroundPosition === 'right' ? 'right center' :
                        style.backgroundPosition === 'expand' ? 'center center' :
                        'center center';
      body.style.backgroundPosition = bgPosition;

      // V2 background repeat logic
      body.style.backgroundRepeat = style.backgroundTiled ? 'repeat' :
                                   (style.backgroundPosition === 'expand' ? 'no-repeat' : 'repeat');

      // V2 uses 'cover' for expand, 'auto' otherwise
      body.style.backgroundSize = style.backgroundPosition === 'expand' ? 'cover' : 'auto';

      // V2 uses fixed attachment for better scrolling experience
      body.style.backgroundAttachment = 'fixed';
    }

    // Make sure containers are transparent
    const mainContainer = document.getElementById('main-container');
    const mainContent = document.getElementById('main-content');
    const publicEventRoot = document.getElementById('public-event-root');

    if (mainContainer) mainContainer.style.backgroundColor = 'transparent';
    if (mainContent) mainContent.style.backgroundColor = 'transparent';
    if (publicEventRoot) publicEventRoot.style.backgroundColor = 'transparent';

    // Cleanup function
    return () => {
      if (style) {
        body.style.backgroundColor = '';
        body.style.backgroundImage = '';
        body.style.backgroundPosition = '';
        body.style.backgroundRepeat = '';
        body.style.backgroundSize = '';
        body.style.backgroundAttachment = '';
      }
    };
  }, [style, isV1Event]);

  // Render V1 provider only for V1 events
  return (
    <>
      {isV1Event && <V1BackgroundStyleProvider style={style} />}
      {children}
    </>
  );
};